import * as React from 'react';
import { toast } from 'sonner';
import { FileText, Trash2, Upload, Download } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { formatDate } from '../../../design-system/format';
import { hrApi } from '../api';
import type { EmployeeDocument, EmployeeDocumentCategory } from '../types';

const CATEGORIES: { value: EmployeeDocumentCategory; label: string }[] = [
  { value: 'contract', label: 'Contract' },
  { value: 'id', label: 'ID' },
  { value: 'certification', label: 'Certification' },
  { value: 'other', label: 'Other' },
];

function categoryLabel(c: EmployeeDocumentCategory): string {
  return CATEGORIES.find((x) => x.value === c)?.label ?? c;
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

/**
 * Documents section for the employee detail page. `canWrite` controls
 * upload/delete; the list itself is only rendered when the caller already
 * holds the read gate (server-enforced — this is display-only).
 */
export function EmployeeDocumentsPanel({ employeeId, canWrite }: { employeeId: string; canWrite: boolean }) {
  const confirm = useConfirm();
  const [docs, setDocs] = React.useState<EmployeeDocument[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [name, setName] = React.useState('');
  const [category, setCategory] = React.useState<EmployeeDocumentCategory>('other');
  const [expiresAt, setExpiresAt] = React.useState('');
  const fileRef = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(() => {
    setLoading(true);
    hrApi.listEmployeeDocuments(employeeId)
      .then(setDocs)
      .catch((err: any) => toast.error('Could not load documents', { description: err.message }))
      .finally(() => setLoading(false));
  }, [employeeId]);

  React.useEffect(() => { load(); }, [load]);

  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Choose a file first'); return; }
    setUploading(true);
    try {
      await hrApi.uploadEmployeeDocument(employeeId, file, {
        name: name.trim() || file.name,
        category,
        expires_at: expiresAt || null,
      });
      toast.success('Document uploaded');
      setName(''); setCategory('other'); setExpiresAt('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err: any) {
      toast.error('Upload failed', { description: err.message });
    } finally { setUploading(false); }
  }

  async function remove(doc: EmployeeDocument) {
    const ok = await confirm({
      title: 'Delete document?',
      description: `"${doc.name}" will be permanently removed.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await hrApi.deleteEmployeeDocument(employeeId, doc.id);
      toast.success('Document deleted');
      load();
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
    }
  }

  return (
    <div>
      {canWrite && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5 pb-5 border-b border-border/40">
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Signed offer letter" />
          </div>
          <div className="grid gap-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as EmployeeDocumentCategory)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Expires (optional)</Label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
          <div className="sm:col-span-4 flex items-center gap-3">
            <input ref={fileRef} type="file" className="text-[12.5px] text-muted-foreground flex-1" />
            <Button type="button" onClick={upload} disabled={uploading} className="rounded-full h-9 px-4 text-[13px] font-semibold gap-1.5">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-muted-foreground">Loading documents…</p>
      ) : !docs || docs.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">No documents on file.</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-3 rounded-xl ring-1 ring-border/40 px-4 py-3">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-medium text-foreground truncate">{d.name}</p>
                <p className="text-[11.5px] text-muted-foreground mt-0.5">
                  {categoryLabel(d.category)}
                  {d.expires_at && (
                    <span className={isExpired(d.expires_at) ? 'text-destructive font-medium' : ''}>
                      {' · '}{isExpired(d.expires_at) ? 'Expired' : 'Expires'} {formatDate(d.expires_at)}
                    </span>
                  )}
                </p>
              </div>
              <a
                href={d.blob_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                aria-label={`Download ${d.name}`}
              >
                <Download className="h-3.5 w-3.5" />
              </a>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => remove(d)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  aria-label={`Delete ${d.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
